import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { Modal, Button } from "react-bootstrap";
import useAccess from "../../hooks/useAccess";

const parseJsonSafe = (raw) => {
  try {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") return JSON.parse(raw);
    if (typeof raw === "number") return [raw];
    if (typeof raw === "object")
      return Array.isArray(raw) ? raw : Object.values(raw);
    return [];
  } catch {
    return [];
  }
};

const mergeUniqueIds = (...groups) =>
  Array.from(new Set(groups.flat().map(Number).filter(Number.isFinite)));
const TASK_PROGRAM_SYNC_ALLOWED_EMAILS = new Set([
  "daniel.chow@newwaytrucking.com",
  "allen.chow@newwaytrucking.com",
]);

const getTokenEmail = (token) => {
  try {
    const encoded = String(token || "").split(".")[1] || "";
    const normalized = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(window.atob(normalized));
    return String(payload?.email || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const getCurrentUserEmail = (token) => {
  try {
    const me = JSON.parse(localStorage.getItem("me") || "{}");
    if (me?.email) return String(me.email).trim().toLowerCase();
  } catch {
    // Fall back to token parsing for older sessions.
  }
  return getTokenEmail(token);
};

const AssignTasksPage = ({ userId }) => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // Data
  const [categories, setCategories] = useState([]);
  const [allTasks, setAllTasks] = useState([]); // used globally
  const [modalTasks, setModalTasks] = useState([]); // used in edit modal
  const [programs, setPrograms] = useState([]);

  // Selections
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);

  // Edit modal
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingTaskIds, setEditingTaskIds] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [positionCategories, setPositionCategories] = useState([]);
  const [editingCategoryIds, setEditingCategoryIds] = useState([]);

  // Filters (bottom table)
  const [filterCategory, setFilterCategory] = useState("");
  const [q, setQ] = useState("");
  const [programQuery, setProgramQuery] = useState("");
  const [programPage, setProgramPage] = useState(1);

  // Pagination (match AssignTrainingPage style)
  const [page, setPage] = useState(1);
  const perPage = 12;
  const programPerPage = 8;
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [isSyncingPrograms, setIsSyncingPrograms] = useState(false);
  const [syncJob, setSyncJob] = useState(null);
  const [isCancelingSync, setIsCancelingSync] = useState(false);
  const syncPollTimerRef = useRef(null);

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    tasks: {
      assign: hasPermission("Task", "Task", "assign"),
      edit: hasPermission("Task", "Task", "edit"),
    },
  };
  const canRunTaskProgramSync = TASK_PROGRAM_SYNC_ALLOWED_EMAILS.has(
    getCurrentUserEmail(token)
  );

  // Load initial data
  useEffect(() => {
    const load = async () => {
      const [cats, tasks, progs, assigned, user] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/categories`, { headers }),
        axios.get(`${BASE_URL}/api/tasks`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/programs`, { headers }),
        axios.get(`${BASE_URL}/api/tasks/assigned/${userId}?includeHidden=1`, {
          headers,
        }),
        axios.get(`${BASE_URL}/api/users/${userId}`, { headers }),
      ]);

      setCategories(cats.data || []);
      setAllTasks(tasks.data || []);
      setPrograms(progs.data || []);

      // Assigned tasks -> start state
      const assignedTaskIds = (assigned.data?.tasks || [])
        .filter((t) => Number(t.uat_visible ?? 1) === 1)
        .map((t) => Number(t.t_id));
      setSelectedTaskIds(assignedTaskIds);

      // Stored program selection on user (JSON)
      const userPrograms = parseJsonSafe(user.data?.task_programs || []);
      let nextProgramIds = userPrograms.map(Number).filter(Number.isFinite);

      // (Optional) If no stored programs, suggest by emp_category
      if (!userPrograms?.length && Array.isArray(progs.data)) {
        const empCats = parseJsonSafe(user.data?.emp_category || []);
        if (empCats.length) {
          const suggested = progs.data
            .filter((p) =>
              (p.rawCategory || []).some((cid) =>
                empCats.map(Number).includes(Number(cid))
              )
            )
            .map((p) => p.id);

          if (suggested.length) {
            nextProgramIds = mergeUniqueIds(nextProgramIds, suggested);
            const merged = new Set(
              progs.data
                .filter((p) => suggested.includes(p.id))
                .flatMap((p) => p.taskIds)
            );
            setSelectedTaskIds((prev) =>
              Array.from(new Set([...prev, ...merged]))
            );
          }
        }
      }

      setSelectedProgramIds(nextProgramIds);
    };

    load().catch(console.error);
  }, [BASE_URL, headers, userId]);

  // Toggle program -> add/remove its taskIds from manual selection view
  const toggleProgram = (progId) => {
    const prog = programs.find((p) => p.id === progId);
    if (!prog) return;

    const isOn = selectedProgramIds.includes(progId);
    if (isOn) {
      setSelectedProgramIds((prev) => prev.filter((x) => x !== progId));
      // Remove this program's tasks from the current selection (but keep any still present via other programs/manual selection)
      setSelectedTaskIds((prev) =>
        prev.filter((tid) => !prog.taskIds.includes(tid))
      );
    } else {
      setSelectedProgramIds((prev) => [...prev, progId]);
      setSelectedTaskIds((prev) =>
        Array.from(new Set([...prev, ...prog.taskIds]))
      );
    }
  };

  // Toggle an individual task (bottom table)
  const toggleTask = (id) =>
    setSelectedTaskIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Open edit modal for a program
  const openEditModal = async (program) => {
    setEditingProgram(program);
    setEditingTaskIds(program.taskIds);
    setEditingCategoryIds(program.rawCategory || []);

    try {
      const [tasksRes, catsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/tasks/editlist`, {
          headers,
          params: { programName: program.name },
        }),
        axios.get(`${BASE_URL}/api/tasks/positioncategories`, { headers }),
      ]);
      setModalTasks(tasksRes.data || []);
      setPositionCategories(catsRes.data || []);
    } catch (err) {
      console.error("❌ Failed to load edit modal data", err);
      setModalTasks([]);
      setPositionCategories([]);
    }

    setShowModal(true);
  };

  // Save edited tasks for a program
  const saveEditedTasks = async () => {
    if (!editingProgram) return;

    try {
      await axios.put(
        `${BASE_URL}/api/tasks/programs/${editingProgram.id}`,
        { taskIds: editingTaskIds, categoryIds: editingCategoryIds },
        { headers }
      );

      // 🧠 Find category names to display (convert IDs to labels)
      const updatedCategoryNames = positionCategories
        .filter((c) => editingCategoryIds.includes(c.c_id))
        .map((c) => c.c_name);

      // 🧩 Update frontend immediately (no refresh needed)
      setPrograms((prev) =>
        prev.map((p) =>
          p.id === editingProgram.id
            ? {
                ...p,
                taskIds: editingTaskIds,
                category: updatedCategoryNames,
                rawCategory: editingCategoryIds,
              }
            : p
        )
      );

      setShowModal(false);
      alert("✔️ Program tasks & categories updated!");
    } catch (err) {
      console.error("❌ Failed to save edited tasks", err);
      alert("⚠️ Failed to save changes. Please try again.");
    }
  };

  // One Save button -> save programs, then manual tasks
  const handleSave = async () => {
    await axios.post(
      `${BASE_URL}/api/tasks/programs/assign`,
      { userId, programIds: selectedProgramIds },
      { headers }
    );

    await axios.post(
      `${BASE_URL}/api/tasks/assign`,
      { userId, taskIds: selectedTaskIds },
      { headers }
    );

    alert("✔️ Saved task programs & assignments.");
  };

  const handleSaveWithLoading = async () => {
    if (isSavingAssignments) return;

    try {
      setIsSavingAssignments(true);
      await handleSave();
    } catch (err) {
      console.error("Failed to save task assignments", err);
      alert("Failed to save task assignments. Please try again.");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  const handleSyncExistingUsers = async () => {
    if (isSyncingPrograms) return;

    try {
      setIsSyncingPrograms(true);
      setIsCancelingSync(false);
      setSyncJob({
        progress: 1,
        message: "Starting task program sync...",
        status: "queued",
      });

      const res = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all`,
        {},
        { headers }
      );

      const jobId = res.data?.jobId;
      if (!jobId) {
        throw new Error("Sync job was not created.");
      }

      setSyncJob(res.data?.job || { id: jobId, progress: 1, status: "queued" });
      pollSyncJob(jobId);
    } catch (err) {
      console.error("Failed to sync existing task programs", err);
      alert(
        err?.response?.data?.message ||
          "Failed to sync existing task programs. Please try again."
      );
      setIsSyncingPrograms(false);
      setSyncJob(null);
    }
  };

  const pollSyncJob = (jobId) => {
    if (syncPollTimerRef.current) {
      clearTimeout(syncPollTimerRef.current);
    }

    syncPollTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/tasks/programs/sync-all/${jobId}`,
          { headers }
        );
        const nextJob = res.data?.job;
        setSyncJob(nextJob);

        if (["queued", "running", "canceling"].includes(nextJob?.status)) {
          pollSyncJob(jobId);
          return;
        }

        setIsSyncingPrograms(false);
        setIsCancelingSync(false);

        if (nextJob?.status === "complete") {
          alert(`Sync complete. Updated ${Number(nextJob.syncedUsers || 0)} users.`);
        } else if (nextJob?.status === "failed") {
          alert(nextJob.error || "Sync failed.");
        }
      } catch (err) {
        console.error("Failed to poll task program sync", err);
        setIsSyncingPrograms(false);
        setIsCancelingSync(false);
        alert("Could not check sync progress. Please refresh and try again.");
      }
    }, 1000);
  };

  const handleCancelSync = async () => {
    if (!syncJob?.id || isCancelingSync) return;

    try {
      setIsCancelingSync(true);
      const res = await axios.post(
        `${BASE_URL}/api/tasks/programs/sync-all/${syncJob.id}/cancel`,
        {},
        { headers }
      );
      setSyncJob(res.data?.job || syncJob);
    } catch (err) {
      console.error("Failed to cancel task program sync", err);
      alert("Failed to cancel sync. It may already be finishing.");
      setIsCancelingSync(false);
    }
  };

  const filteredPrograms = useMemo(() => {
    const term = programQuery.trim().toLowerCase();
    if (!term) return programs;

    return programs.filter((program) => {
      const name = String(program.name || "").toLowerCase();
      const categories = Array.isArray(program.category)
        ? program.category.join(", ").toLowerCase()
        : "";
      return name.includes(term) || categories.includes(term);
    });
  }, [programs, programQuery]);

  const pagedPrograms = useMemo(() => {
    const start = (programPage - 1) * programPerPage;
    return filteredPrograms.slice(start, start + programPerPage);
  }, [filteredPrograms, programPage]);

  // Filtering + paging
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allTasks.filter((t) => {
      const catOK =
        !filterCategory || String(t.t_category_id) === String(filterCategory);
      if (!term) return catOK;
      const title = String(t.t_title || "").toLowerCase();
      const catName = String(t.category_name || "").toLowerCase();
      return catOK && (title.includes(term) || catName.includes(term));
    });
  }, [allTasks, q, filterCategory]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page]);

  useEffect(() => setPage(1), [q, filterCategory]);
  useEffect(() => setProgramPage(1), [programQuery]);
  useEffect(
    () => () => {
      if (syncPollTimerRef.current) {
        clearTimeout(syncPollTimerRef.current);
      }
    },
    []
  );

  // UI helpers
  // const taskTitleById = useMemo(() => {
  //   const m = new Map();
  //   allTasks.forEach((t) => m.set(t.t_id, t.t_title));
  //   return m;
  // }, [allTasks]);

  return (
    <div className="container py-3">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0 text-center flex-grow-1">Assign Tasks</h3>
      </div>

      {/* Programs (top) */}
      <div className="d-flex justify-content-between align-items-end gap-2 mt-3 mb-2">
        <h5 className="m-0">Program Assignment List</h5>
        <div className="d-flex gap-2" style={{ minWidth: 420 }}>
          <input
            className="form-control"
            placeholder="Search program or category"
            value={programQuery}
            onChange={(e) => setProgramQuery(e.target.value)}
          />
          {canRunTaskProgramSync && (
            <button
              className="btn btn-outline-primary"
              onClick={handleSyncExistingUsers}
              disabled={isSyncingPrograms}
            >
              {isSyncingPrograms ? "Syncing..." : "Sync"}
            </button>
          )}
        </div>
      </div>
      <div className="table-responsive mb-4">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th className="text-center">Program Name</th>
              <th className="text-center">Employee Category</th>
              <th className="text-center">Edit Program Tasks</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedPrograms.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  {filteredPrograms.length
                    ? "No task programs on this page."
                    : "No task programs match your search."}
                </td>
              </tr>
            ) : (
              pagedPrograms.map((p) => (
                <tr key={p.id}>
                  <td className="text-center">{p.name}</td>
                  <td className="text-center">
                    {Array.isArray(p.category) ? p.category.join(", ") : ""}
                  </td>
                  <td className="text-center">
                    <div>
                      <button
                        className="btn btn-sm btn-outline-primary ms"
                        onClick={() => openEditModal(p)}
                        disabled={!PERMISSIONS.tasks.edit}
                      >
                        ✏️
                      </button>
                    </div>
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedProgramIds.includes(p.id)}
                      onChange={() => toggleProgram(p.id)}
                      disabled={!PERMISSIONS.tasks.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mb-4">
        <Pagination
          currentPage={programPage}
          totalItems={filteredPrograms.length}
          itemsPerPage={programPerPage}
          onPageChange={setProgramPage}
        />
      </div>

      {/* Tasks (bottom) */}
      <div className="d-flex justify-content-between align-items-end mb-2">
        <h5 className="m-0">User Task Assignment</h5>
        <div className="d-flex gap-2" style={{ minWidth: 420 }}>
          <select
            className="form-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.tc_id} value={c.tc_id}>
                {c.tc_name}
              </option>
            ))}
          </select>
          <input
            className="form-control"
            placeholder="Search task or category"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Task</th>
              <th>Category</th>
              <th className="text-center" style={{ width: 120 }}>
                Assigned
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 ? (
              <tr>
                <td colSpan="3" className="text-center text-muted">
                  No tasks match your filters.
                </td>
              </tr>
            ) : (
              paged.map((t) => (
                <tr key={t.t_id}>
                  <td>{t.t_title}</td>
                  <td>{t.category_name}</td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.includes(t.t_id)}
                      onChange={() => toggleTask(t.t_id)}
                      disabled={!PERMISSIONS.tasks.assign}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination & Save */}
      {/* Pagination & Save */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="flex-grow-1">
          <Pagination
            currentPage={page}
            totalItems={filtered.length}
            itemsPerPage={perPage}
            onPageChange={setPage}
          />
        </div>

        {PERMISSIONS.tasks.assign && (
          <button
            className="btn btn-primary ms-3"
            onClick={handleSaveWithLoading}
            disabled={isSavingAssignments}
          >
            {isSavingAssignments ? "Saving..." : "Save Assignments"}
          </button>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Included Tasks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingProgram ? (
            <>
              <p className="text-muted">
                Editing: <strong>{editingProgram.name}</strong>
              </p>
              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Employee Categories
                </label>
                <Select
                  isMulti
                  value={positionCategories
                    .filter((c) => editingCategoryIds.includes(c.c_id))
                    .map((c) => ({ value: c.c_id, label: c.c_name }))}
                  onChange={(selected) =>
                    setEditingCategoryIds((selected || []).map((s) => s.value))
                  }
                  options={positionCategories.map((c) => ({
                    value: c.c_id,
                    label: c.c_name,
                  }))}
                  placeholder="Search and select categories..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="fw-semibold mb-2">
                  Select Tasks to Include
                </label>
                <Select
                  isMulti
                  value={modalTasks
                    .filter((t) => editingTaskIds.includes(t.t_id))
                    .map((t) => ({ value: t.t_id, label: t.t_title }))}
                  onChange={(selected) =>
                    setEditingTaskIds((selected || []).map((s) => s.value))
                  }
                  options={modalTasks.map((t) => ({
                    value: t.t_id,
                    label: t.t_title,
                  }))}
                  placeholder="Search and select tasks..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </>
          ) : (
            <p>No program selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditedTasks}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isSyncingPrograms} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Syncing Task Programs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            {syncJob?.message || "Updating user tasks from current program lists..."}
          </p>
          <div className="progress" style={{ height: 18 }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${Math.max(1, Number(syncJob?.progress || 1))}%` }}
              aria-valuenow={Number(syncJob?.progress || 1)}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {Math.round(Number(syncJob?.progress || 1))}%
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleCancelSync}
            disabled={isCancelingSync || syncJob?.status === "canceling"}
          >
            {isCancelingSync || syncJob?.status === "canceling"
              ? "Canceling..."
              : "Cancel"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignTasksPage;
